import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { api } from "../api/client";
import { useMutation } from "../hooks/useResource";
import { ErrorBanner } from "../components/common";
import { usePortalAuth } from "./PortalAuthProvider";

/**
 * Self-service password change for a portal login.
 *
 * The server bumps `credentials_updated_at`, which invalidates every token issued
 * before now — including the one that made this request — and clears the cookie in
 * the response. So the dialog ends the local session and returns to sign-in rather
 * than leaving a portal whose next click 401s.
 */

/** The API's own minimum (`PASSWORD_MIN_LENGTH`), stated before the round trip. */
const MIN_LENGTH = 12;

export function PortalPasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { endSession } = usePortalAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const change = useMutation(async () => {
    await api.post("/api/portal/auth/password", { currentPassword: current, newPassword: next });
  });

  const mismatch = confirm !== "" && confirm !== next;
  const tooShort = next !== "" && next.length < MIN_LENGTH;
  const submittable = current !== "" && next.length >= MIN_LENGTH && confirm === next && !change.pending;

  const submit = async () => {
    if (!(await change.run(undefined))) return;
    onClose();
    endSession();
    navigate("/portal/login", { replace: true });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change password</DialogTitle>
      <DialogContent>
        <ErrorBanner error={change.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          Changing your password signs out every device, including this one. Sign back in with the new
          password.
        </Alert>
        <Stack spacing={2}>
          <TextField
            label="Current password"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            fullWidth
          />
          <TextField
            label="New password"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            error={tooShort}
            helperText={`At least ${MIN_LENGTH} characters. A long phrase you can remember beats a short complicated one.`}
            fullWidth
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            error={mismatch}
            helperText={mismatch ? "Does not match." : " "}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={change.pending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!submittable}>
          {change.pending ? "Saving…" : "Change password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
